import { useEffect, useRef } from 'react';
import { createMap, mapOptionSelector, randomMapDraw, getNewMapID } from '../lib/mapUtils.js';
import { distanceMiles, pacePerMile, elevationFeet } from '../lib/format.js';

/**
 * The endless "random run" showcase: flies to a random activity, snake-draws it,
 * reveals its stats, then erases and moves on. Ported from randomRunDisplay /
 * informationViewAnimation in strava-functions.js.
 */
export default function RandomRunMap({ data }) {
  const mapContainerRef = useRef(null);
  const detailsRef = useRef(null);
  const nameRef = useRef(null);
  const distanceRef = useRef(null);
  const speedRef = useRef(null);
  const elevationRef = useRef(null);

  useEffect(() => {
    if (!mapContainerRef.current || !data || data.length === 0) return undefined;

    const map = createMap(mapContainerRef.current, mapOptionSelector('randomDraw'));
    setTimeout(() => map.invalidateSize(), 0);

    const timers = new Set();
    let cancelDraw = null;
    let stopped = false;

    const later = (fn, ms) => {
      const id = setTimeout(() => {
        timers.delete(id);
        if (!stopped) fn();
      }, ms);
      timers.add(id);
      return id;
    };

    const informationViewAnimation = (activity) => {
      const details = detailsRef.current;
      if (!details) return;
      if (window.innerWidth > 992) {
        details.style.transform = 'translate(-100%, -100%)';
        later(() => (details.style.transform = 'translate(0%, -100%)'), 4000);
      } else {
        details.style.transform = 'translate(0%, -100%)';
        later(() => (details.style.transform = 'translate(0%, 0%)'), 4000);
      }

      const fields = [nameRef, distanceRef, speedRef, elevationRef];
      fields.forEach((r) => r.current && (r.current.style.display = 'none'));

      later(() => {
        if (!nameRef.current) return;
        nameRef.current.textContent = activity.name;
        nameRef.current.style.display = 'inline';
      }, 5000);
      later(() => {
        if (!distanceRef.current) return;
        distanceRef.current.textContent = `${distanceMiles(activity)} mi`;
        distanceRef.current.style.display = 'inline';
      }, 5500);
      later(() => {
        if (!speedRef.current) return;
        speedRef.current.textContent = `${pacePerMile(activity)} / mi`;
        speedRef.current.style.display = 'inline';
      }, 6000);
      later(() => {
        if (!elevationRef.current) return;
        elevationRef.current.textContent = `${elevationFeet(activity)} ft`;
        elevationRef.current.style.display = 'inline';
      }, 6500);
    };

    const step = (prevID) => {
      if (stopped) return;
      const id = getNewMapID(data.length, prevID);
      informationViewAnimation(data[id]);
      cancelDraw = randomMapDraw(map, data[id], () => step(id));
    };

    step(-1);

    return () => {
      stopped = true;
      if (cancelDraw) cancelDraw();
      timers.forEach(clearTimeout);
      timers.clear();
      map.remove();
    };
  }, [data]);

  return (
    <div id="randomMapContainer">
      <div id="randomMap" ref={mapContainerRef}></div>
      <div id="activityDetails" ref={detailsRef}>
        <p id="activityName" className="activityText" ref={nameRef}></p>
        <p id="activityDistance" className="activityText" ref={distanceRef}></p>
        <p id="activitySpeed" className="activityText" ref={speedRef}></p>
        <p id="activityElevation" className="activityText" ref={elevationRef}></p>
      </div>
    </div>
  );
}
